import React, { useState, useRef, useEffect } from "react";
import { Send, MoreVertical, X } from "lucide-react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  isThinking?: boolean;
}

interface ChatListItem {
  chatId: string;
  createdAt: string;
  name?: string;
  leagueId?: string;
  playerName?: string;
}

interface ChatPanelProps {
  title: string;
  messages: ChatMessage[];
  onSendMessage: (message: string) => void;
  className?: string;
  isMobile?: boolean;
  chats?: ChatListItem[];
  currentChatId?: string | null;
  onSelectChat?: (chatId: string) => void;
  onNewChat?: () => void;
  onClose?: () => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  title,
  messages,
  onSendMessage,
  className = "",
  isMobile = false,
  chats = [],
  currentChatId = null,
  onSelectChat,
  onNewChat,
  onClose,
}) => {
  const [messageInput, setMessageInput] = useState("");
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Handle keyboard visibility on mobile
  useEffect(() => {
    if (!isMobile) return;

    const handleViewportChange = () => {
      if (window.visualViewport) {
        const keyboardHeight = window.innerHeight - window.visualViewport.height;
        setKeyboardHeight(keyboardHeight);
      }
    };

    window.visualViewport?.addEventListener('resize', handleViewportChange);
    window.visualViewport?.addEventListener('scroll', handleViewportChange);
    
    return () => {
      window.visualViewport?.removeEventListener('resize', handleViewportChange);
      window.visualViewport?.removeEventListener('scroll', handleViewportChange);
    };
  }, [isMobile]);

  const handleSendMessage = () => {
    if (messageInput.trim()) {
      onSendMessage(messageInput);
      setMessageInput("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className={isMobile ? "h-full flex flex-col" : "sticky top-24 h-[calc(100vh-8rem)] overflow-hidden"}>
      <div
        className={`flex h-full w-full flex-col ${isMobile ? "bg-bg-primary" : "rounded-lg border border-border-primary bg-bg-secondary min-h-[500px]"} ${className}`}
      >
        <div className="border-b border-border-primary px-4 py-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-normal font-primary text-text-primary">
              {title}
            </h3>
            <div className="flex items-center gap-2">
              <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors"
                aria-label="Chat menu"
              >
                <MoreVertical size={20} className="text-text-secondary" />
              </button>
              
              {isMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-bg-secondary border border-border-primary rounded-lg shadow-lg z-50">
                  {onNewChat && (
                    <button
                      onClick={() => {
                        onNewChat();
                        setIsMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-bg-tertiary transition-colors border-b border-border-primary flex items-center gap-2"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14M5 12h14"/>
                      </svg>
                      <span className="text-text-primary font-medium">New Chat</span>
                    </button>
                  )}
                  
                  {chats.length > 0 && (
                    <div className="max-h-96 overflow-y-auto">
                      {chats.map((chat) => {
                        const date = new Date(chat.createdAt);
                        const dateStr = date.toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                        let label = dateStr;
                        if (chat.name) {
                          label = `${chat.name}`;
                        } else if (chat.leagueId) {
                          label = `League Chat`;
                        } else if (chat.playerName) {
                          label = chat.playerName;
                        } else {
                          label = 'General Chat';
                        }
                        
                        const isSelected = chat.chatId === currentChatId;
                        
                        return (
                          <button
                            key={chat.chatId}
                            onClick={() => {
                              if (onSelectChat) {
                                onSelectChat(chat.chatId);
                              }
                              setIsMenuOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 hover:bg-bg-tertiary transition-colors ${
                              isSelected ? 'bg-bg-tertiary' : ''
                            }`}
                          >
                            <div className="text-sm text-text-primary">{label}</div>
                            <div className="text-xs text-text-secondary mt-0.5">{dateStr}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  
                  {chats.length === 0 && (
                    <div className="px-4 py-3 text-sm text-text-secondary">
                      No previous chats
                    </div>
                  )}
                </div>
              )}
              </div>
              {isMobile && onClose && (
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-bg-tertiary rounded-lg transition-colors"
                  aria-label="Close chat"
                >
                  <X size={20} className="text-text-secondary" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div 
          ref={messagesContainerRef}
          className={`flex-grow space-y-8 overflow-y-auto p-4`}
          style={isMobile ? { paddingBottom: `${keyboardHeight + 80}px` } : {}}
        >
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex items-start gap-3 ${
                message.isUser ? "justify-end" : ""
              }`}
            >
              {!message.isUser && (
                <div className="h-8 w-8 shrink-0 rounded-full bg-accent-primary p-1.5">
                  <svg
                    fill="none"
                    viewBox="0 0 48 48"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M42.4379 44C42.4379 44 36.0744 33.9038 41.1692 24C46.8624 12.9336 42.2078 4 42.2078 4L7.01134 4C7.01134 4 11.6577 12.932 5.96912 23.9969C0.876273 33.9029 7.27094 44 7.27094 44Z"
                      fill="white"
                    />
                  </svg>
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-lg px-4 py-3 ${
                  message.isUser
                    ? "rounded-tr-none bg-accent-primary"
                    : `rounded-tl-none bg-bg-tertiary ${message.isThinking ? 'border border-border-primary' : ''}`
                }`}
              >
                {message.isUser ? (
                  <p className={`text-sm text-white`}>
                    {message.text}
                  </p>
                ) : message.isThinking ? (
                  <div className="text-sm text-text-secondary italic flex items-start gap-2 opacity-70">
                    <span className="inline-block mt-0.5 shrink-0">
                      <span className="inline-block h-3 w-3 border-2 border-text-secondary/50 border-t-accent-primary rounded-full animate-spin"></span>
                    </span>
                    <span>{message.text}</span>
                  </div>
                ) : (
                  <div className="prose prose-sm max-w-none">
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ children }) => <p className="mb-3 text-sm text-text-primary leading-relaxed last:mb-0">{children}</p>,
                        ul: ({ children }) => <ul className="list-disc pl-6 mb-3 space-y-1 text-sm text-text-primary marker:text-text-secondary">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal pl-6 mb-3 space-y-1 text-sm text-text-primary marker:text-text-secondary">{children}</ol>,
                        li: ({ children }) => <li className="text-sm text-text-primary leading-relaxed">{children}</li>,
                        strong: ({ children }) => <strong className="font-semibold text-text-primary">{children}</strong>,
                        em: ({ children }) => <em className="italic text-text-primary">{children}</em>,
                        h1: ({ children }) => <h1 className="text-xl font-bold mb-3 mt-4 first:mt-0 text-text-primary">{children}</h1>,
                        h2: ({ children }) => <h2 className="text-lg font-semibold mb-2 mt-3 first:mt-0 text-text-primary">{children}</h2>,
                        h3: ({ children }) => <h3 className="text-base font-semibold mb-2 mt-3 first:mt-0 text-text-primary">{children}</h3>,
                        code: ({ inline, children }: any) => 
                          inline ? (
                            <code className="px-1.5 py-0.5 bg-bg-primary/50 border border-border-primary rounded text-xs font-mono text-accent-primary">{children}</code>
                          ) : (
                            <pre className="bg-bg-primary border border-border-primary rounded-lg p-3 overflow-x-auto mb-3 mt-2">
                              <code className="text-xs font-mono text-text-primary leading-relaxed">{children}</code>
                            </pre>
                          ),
                        blockquote: ({ children }) => (
                          <blockquote className="border-l-4 border-accent-primary/30 pl-4 my-3 text-text-secondary italic">{children}</blockquote>
                        ),
                        hr: () => <hr className="my-4 border-border-primary" />,
                        a: ({ children, href }) => (
                          <a href={href} className="text-accent-primary hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>
                        ),
                        table: ({ children }) => (
                          <div className="overflow-x-auto mb-3">
                            <table className="min-w-full divide-y divide-border-primary">{children}</table>
                          </div>
                        ),
                        thead: ({ children }) => <thead className="bg-bg-primary">{children}</thead>,
                        tbody: ({ children }) => <tbody className="divide-y divide-border-primary">{children}</tbody>,
                        tr: ({ children }) => <tr>{children}</tr>,
                        th: ({ children }) => <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">{children}</th>,
                        td: ({ children }) => <td className="px-3 py-2 text-sm text-text-primary">{children}</td>,
                      }}
                    >
                      {message.text}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
              {message.isUser && (
                <div className="h-8 w-8 shrink-0 rounded-full bg-gray-700" />
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div 
          className={`border-t border-border-primary p-4 ${isMobile ? "absolute left-0 right-0 bg-bg-primary" : "mt-auto"}`}
          style={isMobile ? { bottom: `${keyboardHeight}px` } : {}}
        >
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                // Remove auto-scrolling - let the keyboard height adjustment handle positioning
              }}
              className="w-full rounded-md border border-transparent bg-bg-tertiary py-3 pl-4 pr-12 text-base text-text-primary placeholder:text-text-secondary focus:border-accent-primary focus:outline-none focus:ring-1 focus:ring-accent-primary"
              placeholder="Ask me anything..."
              autoComplete="off"
              autoCorrect="off"
            />
            <button
              onClick={handleSendMessage}
              className="absolute inset-y-0 right-0 flex h-full w-12 items-center justify-center text-text-secondary transition-colors hover:text-accent-primary"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
